import MuiButton, { ButtonProps as MuiButtonProps } from "@material-ui/core/Button";
import { ButtonTypeMap as MuiButtonTypeMap } from "@material-ui/core/Button/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import { OverridableComponent, OverrideProps } from "@material-ui/core/OverridableComponent";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import React from "react";
import { HasTestId } from "../../types";
import { neverReachedFor } from "../../util";

export enum ButtonVariant {
    Contextual = "Contextual",
    PageHighlight = "PageHighlight",
    Page = "Page",
    TableHighlight = "TableHighlight",
    Table = "Table",
}

export interface ButtonTypeMap<PropsT = unknown, ComponentT extends React.ElementType = "button"> {
    props: Omit<MuiButtonTypeMap<PropsT, ComponentT>["props"], "color" | "variant"> &
        HasTestId & {
            showProgress?: boolean;
            variant: ButtonVariant;
        };
    defaultComponent: MuiButtonTypeMap<PropsT, ComponentT>["defaultComponent"];
    classKey: MuiButtonTypeMap<PropsT, ComponentT>["classKey"];
}

export type ButtonProps<ComponentT extends React.ElementType = ButtonTypeMap["defaultComponent"], PropsT = unknown> = OverrideProps<ButtonTypeMap<PropsT, ComponentT>, ComponentT>;

export const Button = React.forwardRef(
    <ComponentT extends React.ElementType, PropsT>(
        { children, disabled, showProgress, testId, variant, ...props }: ButtonProps<ComponentT, PropsT>,
        ref: ButtonProps["ref"],
    ): React.ReactElement<ButtonProps<ComponentT, PropsT>> => {
        const classes = useStyles();
        return (
            <MuiButton {...variantAndColor(variant)} data-testid={testId} disabled={disabled || showProgress} ref={ref} {...props}>
                {children}
                {showProgress && <CircularProgress key="circular-progress-indicator" className={classes.buttonProgress} />}
            </MuiButton>
        );
    },
) as OverridableComponent<ButtonTypeMap>;

const variantAndColor = (variant: ButtonVariant): Required<Pick<MuiButtonProps, "variant" | "color">> => {
    switch (variant) {
        case ButtonVariant.Contextual:
            return { variant: "text", color: "default" };
        case ButtonVariant.PageHighlight:
            return { variant: "contained", color: "default" };
        case ButtonVariant.Page:
            return { variant: "outlined", color: "default" };
        case ButtonVariant.TableHighlight:
            return { variant: "contained", color: "secondary" };
        case ButtonVariant.Table:
            return { variant: "outlined", color: "secondary" };
        default:
            return neverReachedFor(variant);
    }
};

const useStyles = makeStyles(
    createStyles({
        buttonProgress: {
            position: "absolute",
            top: "50%",
            left: "50%",
            marginTop: -12,
            marginLeft: -12,
            // IMPORTANT: Do not change the following, making it 0 prevents Chromium from adding a scrollbar for invisible margin rectangles
            marginBottom: 0,
            marginRight: 0,
        },
    }),
);
